import { ActivityStep, TurnActivity } from '@monetize/types/app';
import { getOpenSegment, getRunningStep } from '../../services/chatActivity';

type ToolArgs = Record<string, unknown>;

interface StepCopy {
  /** Shown while the call runs ("Searching contracts"). */
  running: string;
  /** Shown once it has finished ("Searched contracts"). */
  done: string;
}

const parseJson = (text?: string): unknown => {
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const parseArgs = (input?: string): ToolArgs => {
  const parsed = parseJson(input);
  return typeof parsed === 'object' && parsed !== null
    ? (parsed as ToolArgs)
    : {};
};

const humanize = (identifier: string) => identifier.replace(/[_-]+/g, ' ');

const pluralize = (noun: string) =>
  /[^aeiou]y$/.test(noun) ? `${noun.slice(0, -1)}ies` : `${noun}s`;

/** `bill_group` -> "bill group" / "bill groups". Unknown until the call's
 * arguments arrive with `tool_start`, a moment after it is announced. */
const objectNoun = ({ object_type: objectType }: ToolArgs) => {
  if (typeof objectType !== 'string' || !objectType) {
    return undefined;
  }
  const singular = humanize(objectType).toLowerCase();
  return { singular, plural: pluralize(singular) };
};

const sameCopy = (text: string): StepCopy => ({ running: text, done: text });

/**
 * Copy for the agent service's tools, keyed by the exact `tool_name` it
 * streams (monetizenow MCP server, Metabase MCP server, and the agents' own
 * tools). Anything missing here falls back to a humanized tool name, so a
 * new backend tool still reads sensibly before it gets its own entry.
 */
const TOOL_COPY: Record<string, (args: ToolArgs) => StepCopy> = {
  get_current_date: () => ({
    running: "Checking today's date",
    done: "Checked today's date",
  }),
  monetizenow_search_objects: (args) => {
    const noun = objectNoun(args);
    return noun
      ? { running: `Searching ${noun.plural}`, done: `Searched ${noun.plural}` }
      : sameCopy('Searching');
  },
  monetizenow_get_object_search_attributes: (args) => {
    const noun = objectNoun(args);
    return noun
      ? {
          running: `Looking up ${noun.singular} fields`,
          done: `Looked up ${noun.singular} fields`,
        }
      : sameCopy('Looking up fields');
  },
  monetizenow_describe_object: (args) => {
    const noun = objectNoun(args);
    return noun
      ? {
          running: `Reading up on ${noun.plural}`,
          done: `Read up on ${noun.plural}`,
        }
      : sameCopy('Reading up on the data model');
  },
  monetizenow_retrieve_object: (args) => {
    const noun = objectNoun(args);
    return noun
      ? {
          running: `Opening the ${noun.singular}`,
          done: `Opened the ${noun.singular}`,
        }
      : sameCopy('Opening a record');
  },
  monetizenow_pull_crm_entity: () => ({
    running: 'Pulling the account from your CRM',
    done: 'Pulled the account from your CRM',
  }),
  create_net_new_quote: () => ({
    running: 'Creating a quote',
    done: 'Created a quote',
  }),
  update_quote: () => ({
    running: 'Updating the quote',
    done: 'Updated the quote',
  }),
  set_quote_contacts: () => ({
    running: 'Setting the quote contacts',
    done: 'Set the quote contacts',
  }),
  create_quote_offering: () => ({
    running: 'Adding an offering to the quote',
    done: 'Added an offering to the quote',
  }),
  update_quote_offering: () => ({
    running: 'Updating an offering on the quote',
    done: 'Updated an offering on the quote',
  }),
  amend_contract: () => ({
    running: 'Starting a contract amendment',
    done: 'Started a contract amendment',
  }),
  renew_contract: () => ({
    running: 'Starting a contract renewal',
    done: 'Started a contract renewal',
  }),
  update_opportunity: () => ({
    running: 'Updating the opportunity',
    done: 'Updated the opportunity',
  }),
  create_account_based_rate: () => ({
    running: 'Creating an account rate',
    done: 'Created an account rate',
  }),
  get_bill_group_stats: () => ({
    running: 'Checking bill group stats',
    done: 'Checked bill group stats',
  }),
  generate_redirect_signal: (args) => {
    const entity =
      typeof args.entity_name === 'string'
        ? humanize(args.entity_name)
        : 'record';
    return { running: `Opening the ${entity}`, done: `Opened the ${entity}` };
  },
  list_databases: () => ({
    running: 'Listing databases',
    done: 'Listed databases',
  }),
  list_tables: () => ({ running: 'Listing tables', done: 'Listed tables' }),
  get_table_fields: () => ({
    running: 'Reading table fields',
    done: 'Read table fields',
  }),
  get_field_values: () => ({
    running: 'Checking field values',
    done: 'Checked field values',
  }),
  execute_query: () => ({ running: 'Running a query', done: 'Ran a query' }),
  list_cards: () => ({
    running: 'Browsing saved questions',
    done: 'Browsed saved questions',
  }),
  execute_card: () => ({
    running: 'Running a saved question',
    done: 'Ran a saved question',
  }),
  list_collections: () => ({
    running: 'Browsing collections',
    done: 'Browsed collections',
  }),
};

const getStepCopy = ({ toolName = '', input }: ActivityStep): StepCopy => {
  const copy = TOOL_COPY[toolName];
  if (copy) {
    return copy(parseArgs(input));
  }
  const name = humanize(toolName.replace(/^monetizenow_/, '')).trim();
  return sameCopy(
    name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : 'Working',
  );
};

/** "9 found" for a search, "12 rows" for a query. */
const getResultSummary = ({ toolName, resultCount }: ActivityStep) => {
  if (resultCount === undefined) {
    return undefined;
  }
  if (toolName === 'execute_query') {
    return `${resultCount.toLocaleString()} ${resultCount === 1 ? 'row' : 'rows'}`;
  }
  return resultCount === 0
    ? 'None found'
    : `${resultCount.toLocaleString()} found`;
};

export interface StepLabel {
  text: string;
  detail?: string;
}

/** Narration is written as markdown; the timeline shows it as plain text. */
const toPlainText = (text = '') => text.replace(/\*\*|__|`/g, '').trim();

export const getStepLabel = (step: ActivityStep): StepLabel => {
  if (step.kind === 'note') {
    return { text: toPlainText(step.input) };
  }
  const copy = getStepCopy(step);
  if (step.status === 'running') {
    return { text: copy.running };
  }
  if (step.status === 'failed') {
    return { text: copy.running, detail: 'Failed' };
  }
  if (step.status === 'stopped') {
    return { text: copy.running, detail: 'Stopped' };
  }
  return { text: copy.done, detail: getResultSummary(step) };
};

export interface TimelineEntry {
  step: ActivityStep;
  /** The narration the agent wrote ahead of this call, explaining it. */
  reason?: string;
}

/**
 * A sub-agent's narration explains the call that follows it ("No results
 * for CP -- let me broaden the search."), so it goes in that call's details
 * instead of taking a row of its own. Narration with no call after it -- a
 * turn stopped mid-thought -- keeps its row.
 */
export const buildTimeline = (steps: ActivityStep[]): TimelineEntry[] => {
  const entries: TimelineEntry[] = [];
  let notes: ActivityStep[] = [];
  for (const step of steps) {
    if (step.kind === 'note') {
      notes.push(step);
      continue;
    }
    const reason = notes
      .map(({ input }) => toPlainText(input))
      .filter(Boolean)
      .join(' ');
    entries.push({ step, reason: reason || undefined });
    notes = [];
  }
  return [...entries, ...notes.map((step) => ({ step }))];
};

const PHASE_LABELS: Record<TurnActivity['phase'], string> = {
  thinking: 'Thinking',
  drafting: 'Writing up the results',
  finalizing: 'Finishing up',
};

export const RECONNECTING_LABEL = 'Reconnecting…';

/** What the agent is doing right now: the running tool, else the phase. */
export const getLiveActivityLabel = (
  activity: TurnActivity,
  isReconnecting = false,
): string => {
  if (isReconnecting) {
    return RECONNECTING_LABEL;
  }
  const segment = getOpenSegment(activity);
  const runningStep = segment && getRunningStep(segment);
  return runningStep
    ? getStepLabel(runningStep).text
    : PHASE_LABELS[activity.phase];
};

/** "8s", "1m 5s", "1h 2m". */
export const formatElapsed = (milliseconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};
